import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  Card,
  Button,
  Table,
  OverlayTrigger,
  Tooltip,
  Modal,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import {
  CM_PRIMARY_ID,
  DECLINE_REASON,
  ELECTION_TYPE,
  NOMINATION_STATUS,
  REFEREE_STATUS,
  SE_COMMITTEE_ID,
  strapiUrl,
} from '../../../utils/constants';
import DownloadFile from '../Forms/Elements/DownloadFile';
import { getReportRoundYear } from '../../../utils/getReportRoundYear';
import { getNominationRoundYear } from '../../../utils/getNominationRoundYear';
import { getUserSectionalCommittee } from '../../../utils/getUserSectionalCommittee';
import {
  calculateAverageScoreWithCount,
  calculatePendingScoresCount,
} from '../../../utils/candidateScoreCalculations';
import { useAdminCollections } from '../../../context/AdminCollections';
import RankingSelector from './RankingSelector';
import NominationViewProposer from '../NominationViewProposer';
import { useAdminActionCollections } from '../../../context/AdminActionCollections';

const NominationCardGrid = ({
  nomination,
  candidates,
  refereeReports,
  sectionalCommittees,
  fellows,
  nonAasReferees,
  declineReasons,
  refereeStatuses,
  handleSelectClick,
  handleStage2SelectClick,
  handleCOIClick,
  toggleCollapse,
  open,
  setNominationId,
  userDetails,
  showCOIFeatures,
  onRankingSuccess,
  committeeRankings,
  committeeArray,
  coiDeclarationArray,
  onStartTour,
  committeeMedians,
  cardId,
  allocatedNominationIds,
  isCouncilMemberOnly,
  userScore,
  userCOI,
  userRoles,
  currentRound,
  enableCouncilMembers,
}) => {
  const { enableRecommendedToCouncil } = useAdminActionCollections();

  const filteredReports = refereeReports?.filter(
    (report) =>
      report.attributes.nominationId === nomination.id &&
      !report.attributes.refereeArchived,
  );

  const [hasCOIDeclaration, setHasCOIDeclaration] = useState(false);
  const [sectionalCommitteeId, setSectionalCommitteeId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [animationType, setAnimationType] = useState(null);
  const { nominationStateArray, councilMembersArray } = useAdminCollections();

  const assignedCouncilMemberName = useMemo(() => {
    const state = nominationStateArray?.find(
      (s) => (s.attributes?.nominationId || s.nominationId) === nomination.id,
    );

    const userId =
      state?.attributes?.councilMemberUserId || state?.councilMemberUserId;

    if (!userId || userId === -1) return null;

    if (userId === userDetails?.id) return 'You';

    const councilMemberEntry = councilMembersArray?.find((cm) => {
      const idPath1 = cm.attributes?.users_permissions_user?.data?.id;
      const idPath2 = cm.users_permissions_user?.id;
      return idPath1 === userId || idPath2 === userId;
    });

    if (!councilMemberEntry) return 'Assigned';

    const userAttr =
      councilMemberEntry.attributes?.users_permissions_user?.data?.attributes ||
      councilMemberEntry.attributes?.users_permissions_user ||
      councilMemberEntry.users_permissions_user;

    const fellowId = userAttr?.fellowId;

    if (fellowId && fellows?.[fellowId]) {
      return fellows[fellowId];
    }

    if (userAttr?.username) {
      return userAttr.username.split(' (')[0];
    }

    return 'Assigned';
  }, [
    nomination.id,
    nominationStateArray,
    councilMembersArray,
    userDetails?.id,
    fellows,
  ]);

  const prevStatusRef = React.useRef({
    shortlisted: null,
    councilShortlisted: null,
    ranking: null,
  });

  const { refreshCOI, candidateScoresArray, nominationArray } =
    useAdminCollections();

  useEffect(() => {
    const committeeId = getUserSectionalCommittee(userDetails, committeeArray);
    setSectionalCommitteeId(committeeId);
  }, [userDetails, committeeArray]);

  const isOverlapAndRestrictedView = useCallback(
    () =>
      nomination?.isOverlapCommittee &&
      (userRoles?.isSCMember ||
        userRoles?.isSCChair ||
        userRoles?.isCouncilObserver),
    [
      nomination?.isOverlapCommittee,
      userRoles?.isSCMember,
      userRoles?.isSCChair,
      userRoles?.isCouncilObserver,
    ],
  );

  const shouldShowRefereeButton = useCallback(
    () => !isOverlapAndRestrictedView(),
    [isOverlapAndRestrictedView],
  );

  const shouldShowSimpleScore = useCallback(
    () =>
      !nomination?.isOverlapCommittee &&
      userRoles?.isSCMember &&
      !userRoles?.isSCChair,
    [
      nomination?.isOverlapCommittee,
      userRoles?.isSCMember,
      userRoles?.isSCChair,
    ],
  );

  const shouldShowChairScore = useCallback(
    () =>
      !userRoles?.isSCMember &&
      userRoles?.isSCChair &&
      !isOverlapAndRestrictedView(),
    [userRoles?.isSCMember, userRoles?.isSCChair],
  );

  const shouldShowMeanAndMedian = useCallback(
    () =>
      !userRoles?.isSCMember &&
      !isOverlapAndRestrictedView() &&
      !(enableCouncilMembers && userRoles?.isCouncilMember),
    [
      userRoles?.isSCMember,
      userRoles?.isCouncilMember,
      isOverlapAndRestrictedView,
      enableCouncilMembers,
    ],
  );

  const checkCOIDeclaration = useCallback(() => {
    if (
      !showCOIFeatures ||
      !userDetails?.id ||
      !nomination.attributes.candidateId
    ) {
      setHasCOIDeclaration(false);
      return;
    }

    const coiCurrentRound = getNominationRoundYear(new Date());

    const hasDeclaration =
      coiDeclarationArray &&
      coiDeclarationArray.some((declaration) => {
        const baseMatch =
          declaration.attributes.nominationId === nomination.id &&
          declaration.attributes.candidateId ===
            nomination.attributes.candidateId &&
          declaration.attributes.nominationRound === coiCurrentRound;

        if (userRoles?.isAdmin) {
          return baseMatch;
        }
        if (
          userRoles?.isSCChair ||
          userRoles?.isSCMember ||
          userRoles?.isCouncilMember
        ) {
          return baseMatch && declaration.attributes.userId === userDetails.id;
        }

        return false;
      });

    setHasCOIDeclaration(hasDeclaration);
  }, [
    nomination.attributes.candidateId,
    nomination.id,
    userDetails?.id,
    showCOIFeatures,
    coiDeclarationArray,
    userRoles,
  ]);

  const getCommitteeIdWithElectionTypeMapping = useCallback(() => {
    if (
      nomination.attributes.electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION
    ) {
      return SE_COMMITTEE_ID; // Special Election
    }
    if (
      nomination.attributes.electionTypeId ===
      ELECTION_TYPE.CORRESPONDING_MEMBER
    ) {
      return CM_PRIMARY_ID; // Corresponding Membership
    }
    return nomination.attributes.sectionalCommitteeId || sectionalCommitteeId;
  }, [
    nomination.attributes.electionTypeId,
    nomination.attributes.sectionalCommitteeId,
    sectionalCommitteeId,
  ]);

  const candidateScoreData = useMemo(
    () =>
      calculateAverageScoreWithCount(
        nomination.attributes.candidateId,
        candidateScoresArray,
      ),
    [nomination.attributes.candidateId, candidateScoresArray],
  );

  const pendingScoresCount = useMemo(
    () =>
      calculatePendingScoresCount(
        nomination.attributes.candidateId,
        getCommitteeIdWithElectionTypeMapping(),
        committeeArray,
        candidateScoresArray,
        coiDeclarationArray,
        currentRound,
      ),
    [
      nomination.attributes.candidateId,
      getCommitteeIdWithElectionTypeMapping,
      committeeArray,
      candidateScoresArray,
      coiDeclarationArray,
      currentRound,
    ],
  );

  const getScoreDisplay = () => {
    if (candidateScoreData !== null) {
      const scoreText = candidateScoreData.count === 1 ? 'score' : 'scores';
      const baseScore = `${candidateScoreData.average.toFixed(2)} (${candidateScoreData.count} ${scoreText})`;
      const pendingText =
        pendingScoresCount > 0 ? ` (${pendingScoresCount} pending)` : '';
      return baseScore + pendingText;
    }

    if (pendingScoresCount > 0) {
      return `Not scored (${pendingScoresCount} pending)`;
    }

    return 'Not scored';
  };

  const getSimpleScoreDisplay = () => {
    if (userScore?.attributes?.totalScore != null) {
      return userScore.attributes.totalScore.toFixed(2);
    }
    if (userScore?.totalScore != null) {
      return userScore.totalScore.toFixed(2);
    }

    const hasDirectConflict = userCOI?.attributes?.coiTypes?.data?.some(
      (coiType) => coiType.attributes?.category === 'direct',
    );

    if (hasDirectConflict) {
      return 'Not scored (direct conflict declared)';
    }

    return 'Not scored';
  };

  useEffect(() => {
    checkCOIDeclaration();
  }, [checkCOIDeclaration, refreshCOI]);

  const candidateName = candidates[nomination.attributes.candidateId];

  const canPerformCommitteeActions =
    userDetails &&
    (userDetails.roles.includes('committee-chair') ||
      userDetails.roles.includes('admin'));

  const isShortlisted =
    nomination.attributes.nominationStatusTypeId ===
      NOMINATION_STATUS.SHORTLISTED ||
    nomination.attributes.nominationStatusTypeId ===
      NOMINATION_STATUS.RECOMMENDED;

  const isCouncilShortlisted =
    nomination.attributes.nominationStatusTypeId ===
    NOMINATION_STATUS.RECOMMENDED;

  const hasRanking = !!nomination?.attributes?.ranking;

  const canPerformCOIActions =
    showCOIFeatures &&
    handleCOIClick &&
    userDetails &&
    (userDetails.roles.includes('committee-chair') ||
      userDetails.roles.includes('committee-member') ||
      userDetails.roles.includes('council-member')) &&
    (!isCouncilMemberOnly ||
      isCouncilShortlisted ||
      (allocatedNominationIds &&
        allocatedNominationIds.includes(nomination.id)));

  const committeeMedianData =
    committeeMedians?.[getCommitteeIdWithElectionTypeMapping()] || null;

  const hasActionButtons = useMemo(
    () =>
      canPerformCOIActions ||
      (canPerformCommitteeActions && !!handleSelectClick),
    [canPerformCOIActions, canPerformCommitteeActions, handleSelectClick],
  );

  const animationTimeoutRef = useRef(null);
  useEffect(() => {
    if (animationType) {
      return undefined;
    }

    const prev = prevStatusRef.current;

    let nextAnimation = null;
    if (prev.shortlisted === false && isShortlisted === true) {
      nextAnimation = 'shortlisted';
    } else if (
      prev.councilShortlisted === false &&
      isCouncilShortlisted === true
    ) {
      nextAnimation = 'council-shortlisted';
    } else if (prev.ranking !== nomination.attributes.ranking && hasRanking) {
      nextAnimation = 'ranked';
    }

    if (nextAnimation) {
      setAnimationType(nextAnimation);
      animationTimeoutRef.current = setTimeout(() => {
        setAnimationType(null);
        animationTimeoutRef.current = null;
      }, 1500);
    }

    prevStatusRef.current = {
      shortlisted: isShortlisted,
      councilShortlisted: isCouncilShortlisted,
      ranking: nomination.attributes.ranking,
    };

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [
    isShortlisted,
    isCouncilShortlisted,
    hasRanking,
    nomination.attributes.ranking,
    animationType,
  ]);

  const handleViewFormClick = () => {
    setNominationId(nomination.id);
    setShowFormModal(true);
  };

  const handleCloseFormModal = () => {
    setShowFormModal(false);
  };

  const handleStartTour = () => {
    if (onStartTour) {
      onStartTour(hasActionButtons);
    }
  };

  const renderVerticalStepper = () => {
    const getConnector32ClassName = () => {
      if (nomination?.isOverlapCommittee) return 'incomplete';
      if (hasRanking) return 'completed';
      if (isCouncilShortlisted) return 'partial';
      return 'incomplete';
    };

    const getConnector21ClassName = () => {
      if (nomination?.isOverlapCommittee) return 'incomplete';
      if (isCouncilShortlisted) return 'completed';
      if (isShortlisted) return 'partial';
      return 'incomplete';
    };

    const displayHasRanking = hasRanking;
    const displayIsCouncilShortlisted = isCouncilShortlisted;
    const displayIsShortlisted = isShortlisted;

    return (
      <div className="vertical-stepper">
        {/* Stage 3: Ranked Badge */}
        <div
          className={`stepper-badge-container ${displayHasRanking ? 'completed' : 'incomplete'}`}
        >
          <span
            className={`badge ${displayHasRanking ? 'badge-ranked' : 'badge-incomplete'} ${animationType === 'ranked' ? badgeAnimationClass : ''}`}
          >
            {displayHasRanking && <i className="fas fa-trophy me-1" />}
            {!displayHasRanking && 'Not '}Ranked
            {displayHasRanking && `: ${nomination.attributes.ranking}`}
          </span>
        </div>

        {/* Connector 3-2: Ranked to Recommended to Council */}
        <div
          className={`stepper-connector connector-3-2 ${getConnector32ClassName()}`}
        />

        {/* Stage 2: Recommended to Council Badge */}
        <div
          className={`stepper-badge-container ${displayIsCouncilShortlisted ? 'completed' : 'incomplete'}`}
        >
          <span
            className={`badge ${displayIsCouncilShortlisted ? 'badge-council-shortlisted' : 'badge-incomplete'} ${animationType === 'council-shortlisted' ? badgeAnimationClass : ''}`}
          >
            {displayIsCouncilShortlisted && <i className="fas fa-star me-1" />}
            {!displayIsCouncilShortlisted && 'Not '}Recommended to Council
          </span>
        </div>

        {/* Connector 2-1: Recommended to Council to Shortlisted */}
        <div
          className={`stepper-connector connector-2-1 ${getConnector21ClassName()}`}
        />

        {/* Stage 1: Shortlisted Badge */}
        <div
          className={`stepper-badge-container ${displayIsShortlisted ? 'completed' : 'incomplete'}`}
        >
          <span
            className={`badge ${displayIsShortlisted ? 'badge-shortlisted' : 'badge-incomplete'} ${animationType === 'shortlisted' ? badgeAnimationClass : ''}`}
          >
            {displayIsShortlisted && <i className="fas fa-check me-1" />}
            {!displayIsShortlisted && 'Not '}Shortlisted
          </span>
        </div>
      </div>
    );
  };

  const renderShortlistingButtons = () => {
    const isStage1Shortlisted =
      nomination.attributes.nominationStatusTypeId ===
      NOMINATION_STATUS.SHORTLISTED;

    const isStage2ShortlistedLocal =
      nomination.attributes.nominationStatusTypeId ===
      NOMINATION_STATUS.RECOMMENDED;

    const MAX_COUNCIL_RECOMMENDATIONS = 4;

    const getCommitteeIdForNom = (nom) => {
      if (nom.attributes.electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION)
        return 15;
      if (nom.attributes.electionTypeId === ELECTION_TYPE.CORRESPONDING_MEMBER)
        return 14;
      return nom.attributes.sectionalCommitteeId;
    };

    const stage2Count =
      nominationArray?.filter(
        (nom) =>
          nom.attributes.nominationStatusTypeId ===
            NOMINATION_STATUS.RECOMMENDED &&
          getCommitteeIdForNom(nom) === getCommitteeIdWithElectionTypeMapping(),
      ).length || 0;

    const isStage2LimitReached = stage2Count >= MAX_COUNCIL_RECOMMENDATIONS;

    if (
      isStage2ShortlistedLocal &&
      canPerformCommitteeActions &&
      !(nomination?.isOverlapCommittee && userRoles?.isSCChair)
    ) {
      return (
        <RankingSelector
          nominationId={nomination?.id}
          sectionalCommitteeId={getCommitteeIdWithElectionTypeMapping()}
          candidateName={candidateName}
          allCandidates={candidates}
          currentRanking={nomination?.attributes?.ranking}
          onRankingSuccess={(name, rank) =>
            onRankingSuccess(name, rank, nomination?.id)
          }
          committeeRankings={committeeRankings}
          electionTypeId={nomination?.attributes?.electionTypeId}
        />
      );
    }

    if (isStage1Shortlisted && canPerformCommitteeActions) {
      const isDisabled = isStage2LimitReached || !enableRecommendedToCouncil;

      const getTooltipMessage = () => {
        if (!enableRecommendedToCouncil) {
          return 'Council recommendations are currently disabled.';
        }
        if (isStage2LimitReached) {
          return `Maximum of ${MAX_COUNCIL_RECOMMENDATIONS} Council recommendations reached.`;
        }
        return '';
      };

      const button = (
        <Button
          className="standard-width-button"
          variant={isDisabled ? 'outline-success' : 'success'}
          onClick={() =>
            handleStage2SelectClick(
              nomination.id,
              candidateName,
              getCommitteeIdWithElectionTypeMapping(),
              nominationArray,
            )
          }
          disabled={isDisabled}
        >
          {isStage2LimitReached ? (
            <>
              <i className="fas fa-lock me-1" />
              Recommendations Filled
            </>
          ) : (
            'Recommend to Council'
          )}
        </Button>
      );

      return (
        handleStage2SelectClick &&
        !nomination?.isOverlapCommittee &&
        (isDisabled ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id={`tooltip-stage2-limit-${nomination.id}`}>
                {getTooltipMessage()}
              </Tooltip>
            }
          >
            <span className="d-inline-block">{button}</span>
          </OverlayTrigger>
        ) : (
          button
        ))
      );
    }

    if (
      canPerformCommitteeActions &&
      handleSelectClick &&
      !nomination?.isOverlapCommittee
    ) {
      return (
        <Button
          className="standard-width-button"
          variant="success"
          onClick={() => handleSelectClick(nomination.id, candidateName)}
        >
          Shortlist
        </Button>
      );
    }

    return null;
  };

  const cardAnimationClass = animationType
    ? `card-animate-${animationType}`
    : '';
  const badgeAnimationClass = animationType ? 'badge-animate' : '';

  const hasInfoToDisplay = useMemo(() => {
    const hasScores =
      shouldShowSimpleScore() ||
      shouldShowChairScore() ||
      shouldShowMeanAndMedian();
    const hasCommitteeInfo =
      nomination.attributes.sectionalCommitteeId !== null &&
      nomination.attributes.overlapCommittee &&
      nomination.attributes.overlapSectionalCommitteeId !== null;
    const hasCouncilMemberInfo =
      !userRoles?.isSCMember && assignedCouncilMemberName;

    return hasScores || hasCommitteeInfo || hasCouncilMemberInfo;
  }, [
    shouldShowSimpleScore,
    shouldShowChairScore,
    shouldShowMeanAndMedian,
    nomination.attributes.sectionalCommitteeId,
    nomination.attributes.overlapCommittee,
    nomination.attributes.overlapSectionalCommitteeId,
    userRoles?.isSCMember,
    assignedCouncilMemberName,
  ]);

  return (
    <>
      <Card
        id={`card-${cardId}`}
        className={`h-100 nomination-card-grid ${cardAnimationClass}`}
      >
        {/* Card Header */}
        <div className="card-header-grid">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <h4 className="card-title-grid mb-0">{candidateName}</h4>
              {onStartTour && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-help-${nomination.id}`}>
                      Show help for this card
                    </Tooltip>
                  }
                >
                  <i
                    className="bi bi-question-circle text-primary"
                    style={{ cursor: 'pointer', fontSize: '1.1rem' }}
                    onClick={handleStartTour}
                    role="button"
                    tabIndex={0}
                    aria-label="Start tour"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleStartTour();
                      }
                    }}
                  />
                </OverlayTrigger>
              )}
            </div>
            <div className="d-flex gap-2">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-view-${nomination.id}`}>
                    View Nomination
                  </Tooltip>
                }
              >
                <Button
                  variant="link"
                  size="lg"
                  onClick={handleViewFormClick}
                  className="p-0"
                >
                  <i className="bi bi-eye view-button" />
                </Button>
              </OverlayTrigger>
              {shouldShowRefereeButton() && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-referees-${nomination.id}`}>
                      View Referee Reports
                    </Tooltip>
                  }
                >
                  <Button
                    variant="link"
                    size="lg"
                    onClick={() => toggleCollapse(nomination.id)}
                    aria-expanded={open[nomination.id] || false}
                    className="p-0"
                  >
                    <i className="bi bi-people-fill download-button" />
                  </Button>
                </OverlayTrigger>
              )}
            </div>
          </div>
        </div>

        <Card.Body className="d-flex flex-column">
          {!nomination?.isOverlapCommittee && (
            <div className="d-flex justify-content-center">
              {renderVerticalStepper()}
            </div>
          )}

          <div
            className={
              nomination?.isOverlapCommittee === true || !hasInfoToDisplay
                ? 'card-info-section-no-border'
                : 'card-info-section'
            }
          >
            {shouldShowSimpleScore() && (
              <div className="info-row">
                <span className="info-label">Score:</span>
                <span className="info-value">{getSimpleScoreDisplay()}</span>
              </div>
            )}

            {shouldShowChairScore() && (
              <div className="info-row">
                <span className="info-label">Your Score:</span>
                <span className="info-value">{getSimpleScoreDisplay()}</span>
              </div>
            )}

            {shouldShowMeanAndMedian() && (
              <>
                <div className="info-row">
                  <span className="info-label">Mean:</span>
                  <span className="info-value">{getScoreDisplay()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Median:</span>
                  <span className="info-value">
                    {committeeMedianData !== null
                      ? `${committeeMedianData.median.toFixed(2)} (${committeeMedianData.count} ${committeeMedianData.count === 1 ? 'candidate' : 'candidates'})`
                      : 'Not scored'}
                  </span>
                </div>
              </>
            )}

            {nomination.attributes.sectionalCommitteeId !== null &&
              nomination.attributes.overlapCommittee &&
              nomination.attributes.overlapSectionalCommitteeId !== null && (
                <>
                  <div className="info-row">
                    <span className="info-label">Primary:</span>
                    <span className="info-value">
                      {
                        sectionalCommittees[
                          nomination.attributes.sectionalCommitteeId
                        ]
                      }
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Overlap:</span>
                    <span className="info-value">
                      {
                        sectionalCommittees[
                          nomination.attributes.overlapSectionalCommitteeId
                        ]
                      }
                    </span>
                  </div>
                </>
              )}

            {!userRoles?.isSCMember && assignedCouncilMemberName && (
              <div className="info-row">
                <span className="info-label">Council member:</span>
                <span className="info-value">{assignedCouncilMemberName}</span>
              </div>
            )}
          </div>

          <div className="mt-auto">
            <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
              {canPerformCOIActions && !nomination?.isOverlapCommittee && (
                <Button
                  className={`standard-width-button ${hasCOIDeclaration ? 'btn-outline-success' : ''}`}
                  variant={hasCOIDeclaration ? 'outline-success' : 'success'}
                  onClick={() =>
                    handleCOIClick({
                      name: candidateName,
                      candidateId: nomination.attributes.candidateId,
                      nominationId: nomination.id,
                      sectionalCommitteeId:
                        getCommitteeIdWithElectionTypeMapping(),
                    })
                  }
                  title="Conflict of Interest"
                >
                  {hasCOIDeclaration ? (
                    <>
                      <i className="bi bi-check-circle me-1" />
                      COI
                    </>
                  ) : (
                    'COI'
                  )}
                </Button>
              )}

              {renderShortlistingButtons()}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Nomination Form View Modal */}
      <Modal
        show={showFormModal}
        onHide={handleCloseFormModal}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Nomination Form - {candidateName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NominationViewProposer hidePageTitle />
        </Modal.Body>
      </Modal>

      {/* Referee Modal */}
      <Modal
        show={open[nomination.id]}
        onHide={() => toggleCollapse(nomination.id)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Referees for {candidateName}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="referee-overlay-body">
          <Table bordered hover striped size="sm" className="dataTable">
            <thead className="thead-dark">
              <tr>
                <th>
                  <b>Name</b>
                </th>
                <th>
                  <b>Status</b>
                </th>
                <th>
                  <b>Type</b>
                </th>
                <th>
                  <b>Uploaded year</b>
                </th>
                <th aria-label="Actions">
                  <b>Actions</b>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((referee, index) => {
                const reportYear = getReportRoundYear(referee.attributes);
                const candidateNameForFile =
                  candidates[nomination.attributes.candidateId]?.replace(
                    /\s/g,
                    '',
                  ) || '';
                const refereeName = referee.attributes.refereeFellowId
                  ? fellows[referee.attributes.refereeFellowId]
                  : nonAasReferees[referee.attributes.nonAasRefereeId];

                let reason = '';
                const {
                  refereeStatusId,
                  declineReasonId,
                  declineReasonOther,
                  extensionReason,
                  extensionReasonApprovalNote,
                } = referee.attributes;

                if (refereeStatusId === REFEREE_STATUS.DECLINED) {
                  reason =
                    declineReasonId === DECLINE_REASON.OTHER
                      ? declineReasonOther
                      : declineReasons[declineReasonId];
                } else if (
                  refereeStatusId === REFEREE_STATUS.EXTENSION_REQUESTED
                ) {
                  reason = extensionReason;
                } else if (
                  refereeStatusId === REFEREE_STATUS.EXTENSION_APPROVED
                ) {
                  reason = extensionReasonApprovalNote;
                }

                return (
                  <tr key={index}>
                    <td>{refereeName}</td>
                    <td>
                      {reason ? (
                        <>
                          <span>
                            <b>
                              {
                                refereeStatuses[
                                  referee.attributes.refereeStatusId
                                ]
                              }
                              {': '}
                            </b>
                          </span>
                          {refereeStatusId ===
                          REFEREE_STATUS.EXTENSION_APPROVED ? (
                            <>
                              <br />
                              <span>Reason: {extensionReason}</span>
                              <br />
                              <span>
                                Approval Note: {extensionReasonApprovalNote}
                              </span>
                            </>
                          ) : (
                            <span>{reason}</span>
                          )}
                        </>
                      ) : (
                        <span>
                          {refereeStatuses[referee.attributes.refereeStatusId]}
                        </span>
                      )}
                    </td>
                    <td>
                      {referee.attributes.independentAssessor
                        ? 'Independent Assessor'
                        : 'Referee'}
                    </td>
                    <td>{reportYear}</td>
                    <td className="text-center">
                      {referee.attributes.report.data !== null && (
                        <DownloadFile
                          text={`Report Uploaded Year: ${reportYear}`}
                          url={`${strapiUrl}${referee.attributes.report.data.attributes.url}`}
                          name={`${candidateNameForFile}${refereeName}`}
                          buttonClassName="action-button-two"
                          iconClassName="download-button"
                          showFileName={false}
                          showText={false}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    </>
  );
};

NominationCardGrid.propTypes = {
  nomination: PropTypes.object.isRequired,
  candidates: PropTypes.object.isRequired,
  refereeReports: PropTypes.array.isRequired,
  sectionalCommittees: PropTypes.object.isRequired,
  fellows: PropTypes.object.isRequired,
  nonAasReferees: PropTypes.object.isRequired,
  declineReasons: PropTypes.object.isRequired,
  refereeStatuses: PropTypes.object.isRequired,
  handleSelectClick: PropTypes.func.isRequired,
  handleStage2SelectClick: PropTypes.func,
  handleCOIClick: PropTypes.func,
  toggleCollapse: PropTypes.func.isRequired,
  open: PropTypes.object.isRequired,
  setNominationId: PropTypes.func.isRequired,
  userDetails: PropTypes.object,
  showCOIFeatures: PropTypes.bool,
  onRankingSuccess: PropTypes.func,
  committeeRankings: PropTypes.object,
  committeeArray: PropTypes.array,
  coiDeclarationArray: PropTypes.array,
  onStartTour: PropTypes.func,
  committeeMedians: PropTypes.object,
  cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allocatedNominationIds: PropTypes.array,
  isCouncilMemberOnly: PropTypes.bool,
  userScore: PropTypes.object,
  userCOI: PropTypes.object,
  userRoles: PropTypes.shape({
    isSCMember: PropTypes.bool,
    isSCChair: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isCouncilMember: PropTypes.bool,
    isCouncilObserver: PropTypes.bool,
  }),
  currentRound: PropTypes.number,
  enableCouncilMembers: PropTypes.bool,
};

export default NominationCardGrid;
